import {CommonModule, formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faEye} from '@fortawesome/free-solid-svg-icons';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {DatePickerModule} from 'primeng/datepicker';

import {ApiService} from '../../services/api.service';
import {AuthService} from '../../services/auth.service';
import {RoleService} from '../../services/role.service';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';
import {VtePaginationComponent} from '../../shared/vte/vte-pagination/vte-pagination.component';

@Component({
    selector: 'app-lead-list',
    imports: [CommonModule, FormsModule, FontAwesomeModule, DatePickerModule, VtePaginationComponent],
    templateUrl: './lead-list.component.html',
    styleUrls: ['../lead-management.shared.css']
})
export class LeadListComponent implements OnInit {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  leads: any[] = [];
  assignees: any[] = [];
  organizations: any[] = [];
  loading = false;
  currentPage = 1;
  pageSize = 10;
  totalRecords = 0;
  totalPages = 1;
  nextPage: string | null = null;
  previousPage: string | null = null;
  canFilterOrganization = false;
  selectedLead: any = null;
  showFollowUpModal = false;
  showDemoModal = false;
  showDeleteLeadModal = false;
  deletingLead = false;
  leadPendingDelete: any = null;

  filters: any = {
    search: '',
    status: '',
    source: '',
    assigned_to: '',
    date_from: null,
    date_to: null,
    organization: '',
    followup: '',
  };

  followUpForm: any = {
    follow_up_type: 'CALL',
    follow_up_date: '',
    notes: '',
    next_follow_up_date: '',
  };

  demoForm: any = {
    demo_datetime: '',
    demo_type: 'ONLINE',
    demo_status: 'SCHEDULED',
    demo_notes: '',
    remind_one_hour_before: false,
  };

  statuses = [
    ['NEW', 'New'],
    ['CONTACTED', 'Contacted'],
    ['REQUIREMENT_UNDERSTOOD', 'Requirement Understood'],
    ['DEMO_SCHEDULED', 'Demo Scheduled'],
    ['DEMO_COMPLETED', 'Demo Completed'],
    ['NEGOTIATION', 'Negotiation'],
      ['QUOTE_SENT', 'Quote Sent'],
    ['WON', 'Won'],
    ['LOST', 'Lost'],
    ['ON_HOLD', 'On Hold'],
  ];

  sources = [
    ['INDIA_MART', 'IndiaMART'],
    ['WEBSITE', 'Website'],
    ['DIRECT_CALL', 'Direct Call'],
    ['WHATSAPP', 'WhatsApp'],
    ['REFERRAL', 'Referral'],
    ['SALES_INSTALLATION_PARTNER', 'Sales & Installation Partner'],
    ['OTHER', 'Other'],
  ];

  followUpTypes = [
    ['CALL', 'Call'],
    ['WHATSAPP', 'WhatsApp'],
    ['EMAIL', 'Email'],
    ['MEETING', 'Meeting'],
    ['DEMO', 'Demo'],
    ['OTHER', 'Other'],
  ];

  demoTypes = [
    ['ONLINE', 'Online'],
    ['ON_SITE', 'On-site'],
  ];

  demoStatuses = [
    ['SCHEDULED', 'Scheduled'],
    ['COMPLETED', 'Completed'],
    ['CANCELLED', 'Cancelled'],
  ];

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private roleService: RoleService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    library: FaIconLibrary,
  ) {
    library.addIcons(faEye);
  }

  ngOnInit(): void {
    const groups = this.authService.getCurrentUserInfo()?.role;
    this.canFilterOrganization = this.roleService.isSuperUser(groups);
    this.loadAssignees();
    if (this.canFilterOrganization) {
      this.loadOrganizations();
    }
    this.route.queryParamMap.subscribe((params) => {
      this.filters = {
        ...this.filters,
        search: params.get('search') || '',
        status: params.get('status') || '',
        source: params.get('source') || '',
        assigned_to: params.get('assigned_to') || '',
        date_from: this.parseDate(params.get('date_from')),
        date_to: this.parseDate(params.get('date_to')),
        organization: params.get('organization') || '',
        followup: params.get('followup') || '',
      };
      this.currentPage = Number(params.get('page') || 1);
      this.loadLeads();
    });
  }

  loadLeads(): void {
    this.loading = true;
    this.apiService.listLeads(this.currentPage, this.pageSize, this.filters).subscribe({
      next: (response: any) => {
        this.leads = response?.results || [];
        this.totalRecords = response?.count || 0;
        this.totalPages = Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
        this.nextPage = response?.next || null;
        this.previousPage = response?.previous || null;
        this.loading = false;
      },
      error: () => {
        this.leads = [];
        this.loading = false;
      }
    });
  }

  loadAssignees(): void {
    this.apiService.getAssignableUsers(1, 100, '').subscribe({
      next: (response: any) => {
          const results = response?.results || [];
          this.assignees = results.filter(
              (user: any) => user?.id && (user.username || user.first_name || user.last_name),
          );
      }
    });
  }

    assigneeLabel(assignee: any): string {
        const name = `${assignee?.first_name || ''} ${assignee?.last_name || ''}`.trim();
        return name || assignee?.username || 'Unknown';
    }

  loadOrganizations(): void {
    this.apiService.getOrganizations().subscribe({
      next: (response: any) => {
        this.organizations = response?.results || [];
      }
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadLeads();
  }

  resetFilters(): void {
    this.filters = {
      search: '',
      status: '',
      source: '',
      assigned_to: '',
      date_from: null,
      date_to: null,
      organization: '',
      followup: '',
    };
    this.applyFilters();
  }

  changePage(step: number): void {
    const nextPage = this.currentPage + step;
    if (nextPage < 1 || nextPage > this.totalPages) {
      return;
    }
    this.currentPage = nextPage;
    this.loadLeads();
  }

  next(): void {
    if (!this.nextPage) {
      return;
    }
    this.currentPage++;
    this.loadLeads();
  }

  previous(): void {
    if (!this.previousPage) {
      return;
    }
    this.currentPage--;
    this.loadLeads();
  }

  selectPageSize(): void {
    this.currentPage = 1;
    this.loadLeads();
  }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.loadLeads();
    }

    onPageSizeChange(pageSize: number): void {
        this.pageSize = pageSize;
        this.currentPage = 1;
        this.loadLeads();
    }

  openCreate(): void {
    this.router.navigate(['lead-management/leads/new']);
  }

  openView(lead: any): void {
    this.router.navigate([`lead-management/leads/${lead.id}`]);
  }

  openEdit(lead: any): void {
    this.router.navigate([`lead-management/leads/${lead.id}/edit`]);
  }

  deleteLead(lead: any): void {
    if (!lead?.id) {
      return;
    }
    this.leadPendingDelete = lead;
    this.showDeleteLeadModal = true;
  }

  closeDeleteLeadModal(): void {
    if (this.deletingLead) {
      return;
    }
    this.showDeleteLeadModal = false;
    this.leadPendingDelete = null;
  }

  confirmDeleteLead(): void {
    if (!this.leadPendingDelete?.id || this.deletingLead) {
      return;
    }
    this.deletingLead = true;
    this.apiService.deleteLead(this.leadPendingDelete.id).subscribe({
      next: () => {
        this.deletingLead = false;
        this.showDeleteLeadModal = false;
        this.leadPendingDelete = null;
        this.toastr.success('Lead deleted successfully.', 'Success');
        if (this.leads.length === 1 && this.currentPage > 1) {
          this.currentPage--;
        }
        this.loadLeads();
      },
      error: (err) => {
        this.deletingLead = false;
        const message = err?.error?.detail || 'Unable to delete lead.';
        this.toastr.error(message, 'Error');
      }
    });
  }

  openFollowUp(lead: any): void {
    this.selectedLead = lead;
    this.followUpForm = {
      follow_up_type: 'CALL',
      follow_up_date: '',
      notes: '',
      next_follow_up_date: '',
    };
    this.showFollowUpModal = true;
  }

  openDemo(lead: any): void {
    this.selectedLead = lead;
    this.demoForm = {
      demo_datetime: '',
      demo_type: 'ONLINE',
      demo_status: 'SCHEDULED',
      demo_notes: '',
      remind_one_hour_before: false,
    };
    this.showDemoModal = true;
  }

  saveDemo(): void {
    if (!this.selectedLead) {
      return;
    }
    this.apiService.addLeadDemo(this.selectedLead.id, this.serializeDemoForm(this.demoForm)).subscribe({
      next: () => {
        this.showDemoModal = false;
        this.loadLeads();
      }
    });
  }

  saveFollowUp(): void {
    if (!this.selectedLead) {
      return;
    }
    this.apiService.addLeadFollowUp(this.selectedLead.id, this.serializeFollowUpForm(this.followUpForm)).subscribe({
      next: () => {
        this.showFollowUpModal = false;
        this.loadLeads();
      }
    });
  }

  private parseDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private serializeDate(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    return formatDate(date, 'yyyy-MM-dd', 'en-IN');
  }

  private serializeDateTime(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    return formatDate(date, "yyyy-MM-dd'T'HH:mm:ss", 'en-IN');
  }

  private serializeFollowUpForm(form: any): any {
    return {
      ...form,
      follow_up_date: this.serializeDate(form.follow_up_date),
      next_follow_up_date: form.next_follow_up_date ? this.serializeDate(form.next_follow_up_date) : null,
    };
  }

  private serializeDemoForm(form: any): any {
    return {
      ...form,
      demo_datetime: this.serializeDateTime(form.demo_datetime),
    };
  }
}
